import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Progress } from '../ui/progress';
import { AlertTriangle, Package, Box, Projector } from 'lucide-react';

/**
 * Preview-then-commit modal for .avproj imports.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (open) => void
 *   pkg: result of inspectPackage()
 *   onConfirm: ({ projectName, onProgress }) => Promise — caller drives commitImport
 */
export default function ImportProjectModal({ open, onOpenChange, pkg, onConfirm }) {
  const [projectName, setProjectName] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ pct: 0, label: '' });
  const [error, setError] = useState(null);

  const manifest = pkg?.manifest || {};
  const stats = manifest.stats || {};
  const missing = manifest.missingAssets || [];

  // Reset name when a new package is loaded
  React.useEffect(() => {
    if (pkg && open) {
      setProjectName(`${manifest.projectName || 'Imported Project'} (Imported)`);
      setError(null);
      setProgress({ pct: 0, label: '' });
      setBusy(false);
    }
  }, [pkg, open, manifest.projectName]);

  const handleConfirm = async () => {
    if (!onConfirm || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm({
        projectName: projectName.trim() || 'Imported Project',
        onProgress: (pct, label) => setProgress({ pct, label }),
      });
      // Caller handles closing on success
    } catch (e) {
      setError(e?.message || 'Import failed.');
      setBusy(false);
    }
  };

  if (!pkg) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-xl" data-testid="import-project-modal">
        <DialogHeader>
          <DialogTitle className="text-zinc-100 flex items-center gap-2">
            <Package className="w-5 h-5 text-cyan-400" />
            Import Project
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Review the package contents before importing as a new project.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview thumbnail */}
          {pkg.previewUrl && (
            <div className="rounded-md overflow-hidden border border-zinc-700 bg-zinc-950 aspect-video">
              <img
                src={pkg.previewUrl}
                alt="Project preview"
                className="w-full h-full object-cover"
                data-testid="import-preview-thumbnail"
              />
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded border border-zinc-700 bg-zinc-800/50 p-2">
              <div className="text-zinc-500 uppercase tracking-wide text-[10px]">Source</div>
              <div className="text-zinc-200 font-medium truncate" title={manifest.projectName}>
                {manifest.projectName || 'Untitled'}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                Exported {manifest.exportedAt ? new Date(manifest.exportedAt).toLocaleDateString() : '—'}
                {manifest.version && ` · v${manifest.version}`}
              </div>
            </div>
            <div className="rounded border border-zinc-700 bg-zinc-800/50 p-2">
              <div className="text-zinc-500 uppercase tracking-wide text-[10px]">Contents</div>
              <div className="flex items-center gap-3 text-zinc-200 mt-1">
                <span className="flex items-center gap-1"><Box className="w-3 h-3 text-cyan-400" /> {stats.sceneObjects ?? 0}</span>
                <span className="flex items-center gap-1"><Projector className="w-3 h-3 text-fuchsia-400" /> {stats.projectors ?? 0}</span>
                <span className="text-zinc-500">·</span>
                <span className="text-zinc-300">{stats.assets ?? 0} assets</span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {((stats.assetBytes ?? 0) / (1024 * 1024)).toFixed(1)} MB
              </div>
            </div>
          </div>

          {/* Missing assets warning */}
          {missing.length > 0 && (
            <div className="rounded border border-amber-700/60 bg-amber-950/40 p-2.5 text-xs text-amber-300 flex items-start gap-2" data-testid="import-missing-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">{missing.length} asset{missing.length === 1 ? '' : 's'} missing</div>
                <div className="text-amber-400/80 text-[10px] mt-0.5">
                  These were unavailable when the package was exported and may render as missing textures.
                  Re-link them from the Library after import.
                </div>
              </div>
            </div>
          )}

          {/* Project name */}
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">New project name</Label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              disabled={busy}
              className="bg-zinc-800 border-zinc-700 text-zinc-100 text-sm"
              data-testid="import-project-name-input"
              autoFocus
            />
            <div className="text-[10px] text-zinc-500">
              Imported as a new project — your current scene is not affected.
            </div>
          </div>

          {/* Progress / errors */}
          {busy && (
            <div className="space-y-1.5" data-testid="import-progress">
              <Progress value={progress.pct} className="h-2" />
              <div className="text-[11px] text-zinc-400">{progress.label || 'Working…'}</div>
            </div>
          )}
          {error && (
            <div className="rounded border border-red-700/60 bg-red-950/40 p-2 text-xs text-red-300" data-testid="import-error">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="import-cancel-btn"
          >Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={busy || !projectName.trim()}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="import-confirm-btn"
          >
            {busy ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
